import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '../utils/config';
import { Phase1Request } from '../types';
import { useTranslation } from '../context/TranslationContext';
import AdminRequestModal from './AdminRequestModal';
import SupplierBulkImportModal from './SupplierBulkImportModal';
import CompletedRequestDetailModal from './CompletedRequestDetailModal';
import '../styles/AdminTable.css';

interface AdminTableData {
  id: string;
  requestId: string;
  item: string;
  requester: string;
  company: string;
  budget: number;
  currency: string;
  priority: 'Low' | 'Medium' | 'High' | 'Critical';
  submittedDate: string;
}

interface Issue {
  _id: string;
  title: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  user_name: string;
  user_email: string;
  company_name?: string;
  timestamp: string;
  created_at: string;
}

interface AdminTableProps {
  data?: AdminTableData[];
  onApprove?: (id: string, selectedSuppliers?: any[]) => void;
  onReject?: (id: string) => void;
  onViewDetails?: (item: AdminTableData) => void;
}

const AdminTable: React.FC<AdminTableProps> = ({ 
  data: propData, 
  onApprove, 
  onReject, 
  onViewDetails 
}) => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState<AdminTableData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<Phase1Request | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCompletedRequest, setSelectedCompletedRequest] = useState<any>(null);
  const [isCompletedModalOpen, setIsCompletedModalOpen] = useState(false);
  const [isSupplierBulkImportOpen, setIsSupplierBulkImportOpen] = useState(false);
  const [sortField, setSortField] = useState<keyof AdminTableData>('submittedDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [currentView, setCurrentView] = useState<'requests' | 'issues' | 'completed'>('requests');
  const [issues, setIssues] = useState<Issue[]>([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [completedRequests, setCompletedRequests] = useState<any[]>([]);
  const [completedLoading, setCompletedLoading] = useState(false);
  const [companyNames, setCompanyNames] = useState<{[key: string]: string}>({});
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRequests, setTotalRequests] = useState(0);
  const [requestsPerPage] = useState(20);

  // Server-side filtering state
  const [serverFilters, setServerFilters] = useState({
    search: '',
    urgency: '',
    budgetMin: '',
    budgetMax: '',
    dateFrom: '',
    dateTo: '',
    // Column-specific filters
    filterRequestId: '',
    filterItem: '',
    filterRequester: '',
    filterCompany: '',
    filterBudget: '',
    filterCurrency: '',
    filterPriority: ''
  });

  // Column filtering state (for local display)
  const [columnFilters, setColumnFilters] = useState<{
    [key: string]: string;
  }>({
    requestId: '',
    item: '',
    requester: '',
    company: '',
    budget: '',
    currency: '',
    priority: '',
    submittedDate: ''
  });

  // Active filter dropdown
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  const handleSort = (field: keyof AdminTableData) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Column filter handlers (server-side)
  const handleColumnFilter = (field: string, value: string) => {
    // Update both local and server filters
    setColumnFilters(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Map column filter fields to server filter fields
    const fieldMapping: { [key: string]: string } = {
      'requestId': 'filterRequestId',
      'item': 'filterItem',
      'requester': 'filterRequester',
      'department': 'filterDepartment',
      'budget': 'filterBudget',
      'priority': 'filterPriority',
      'status': 'filterStatus'
    };
    
    const serverField = fieldMapping[field];
    if (serverField) {
      setServerFilters(prev => ({
        ...prev,
        [serverField]: value
      }));
      setCurrentPage(1); // Reset to first page when filtering
    }
  };

  const handleFilterToggle = (field: string) => {
    setActiveFilter(activeFilter === field ? null : field);
  };

  const clearAllFilters = () => {
    setColumnFilters({
      requestId: '',
      item: '',
      category: '',
      requester: '',
      department: '',
      budget: '',
      priority: '',
      status: '',
      submittedDate: ''
    });
    setActiveFilter(null);
  };

  const clearColumnFilter = (field: string) => {
    setColumnFilters(prev => ({
      ...prev,
      [field]: ''
    }));
    
    // Also clear server-side filter
    const fieldMapping: { [key: string]: string } = {
      'requestId': 'filterRequestId',
      'item': 'filterItem',
      'requester': 'filterRequester',
      'department': 'filterDepartment',
      'budget': 'filterBudget',
      'priority': 'filterPriority',
      'status': 'filterStatus'
    };
    
    const serverField = fieldMapping[field];
    if (serverField) {
      setServerFilters(prev => ({
        ...prev,
        [serverField]: ''
      }));
      setCurrentPage(1);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Server-side filter handlers
  const handleServerFilterChange = (filterType: string, value: string) => {
    setServerFilters(prev => ({ ...prev, [filterType]: value }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  const clearServerFilters = () => {
    // Clear server-side filters
    setServerFilters({
      search: '',
      urgency: '',
      budgetMin: '',
      budgetMax: '',
      dateFrom: '',
      dateTo: '',
      // Column-specific filters
      filterRequestId: '',
      filterItem: '',
      filterRequester: '',
      filterCompany: '',
      filterBudget: '',
      filterCurrency: '',
      filterPriority: ''
    });
    
    // Clear local column filters
    setColumnFilters({
      requestId: '',
      item: '',
      requester: '',
      company: '',
      budget: '',
      currency: '',
      priority: '',
      submittedDate: ''
    });
    
    // Clear other filter states
    setFilterPriority('all');
    setActiveFilter(null);
    
    setCurrentPage(1);
  };

  // Fetch issues from backend
  const fetchIssues = async () => {
    try {
      setIssuesLoading(true);
      const token = localStorage.getItem('auth_token');
      if (!token) return;

              const response = await fetch(API_ENDPOINTS.ISSUES_ADMIN_LIST, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setIssues(data.data.issues || []);
        }
      }
    } catch (error) {
      console.error('Error fetching issues:', error);
    } finally {
      setIssuesLoading(false);
    }
  };

    const fetchCompletedRequests = async () => {
    try {
      setCompletedLoading(true);
      const response = await fetch(`${API_ENDPOINTS.REQUESTS}?status=completed,approved`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        console.log('🔍 DEBUG - Completed requests company_ids:', result.data?.map((req: any) => ({ id: req._id, company_id: req.company_id })));
        setCompletedRequests(result.data || []);
        
        // Fetch company names for all completed requests
        await fetchCompanyNames(result.data || []);
      } else {
        console.error('Failed to fetch completed requests:', result.message);
        setCompletedRequests([]);
      }
    } catch (error) {
      console.error('Error fetching completed requests:', error);
      setCompletedRequests([]);
    } finally {
      setCompletedLoading(false);
    }
  };

  const fetchCompanyNames = async (requests: any[]) => {
    try {
      const companyIds = Array.from(new Set(requests.map(req => req.company_id).filter(Boolean)));
      console.log('🔍 DEBUG - Company IDs to fetch:', companyIds);
      const names: {[key: string]: string} = {};
      
      for (const companyId of companyIds) {
        try {
          console.log(`🔍 DEBUG - Fetching company name for ID: ${companyId}`);
          const response = await fetch(`${API_ENDPOINTS.COMPANIES}/${companyId}/name`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          
          console.log(`🔍 DEBUG - Response status for ${companyId}:`, response.status);
          
          if (response.ok) {
            const result = await response.json();
            console.log(`🔍 DEBUG - Response data for ${companyId}:`, result);
            if (result.success) {
              names[companyId] = result.data.name;
              console.log(`🔍 DEBUG - Company name set for ${companyId}:`, result.data.name);
            }
          } else {
            console.error(`🔍 DEBUG - Response not OK for ${companyId}:`, response.status);
            const errorText = await response.text();
            console.error(`🔍 DEBUG - Error response for ${companyId}:`, errorText);
          }
        } catch (error) {
          console.error(`Error fetching company name for ${companyId}:`, error);
          names[companyId] = 'Unknown Company';
        }
      }
      
      console.log('🔍 DEBUG - Final company names:', names);
      setCompanyNames(names);
    } catch (error) {
      console.error('Error fetching company names:', error);
    }
  };

  // Toggle between requests, issues, and completed view
  const toggleView = (view: 'requests' | 'issues' | 'completed') => {
    setCurrentView(view);
    if (view === 'issues') {
      fetchIssues();
    } else if (view === 'completed') {
      fetchCompletedRequests();
    }
  };

  const sortedAndFilteredData = data
    .filter(item => {
      // Existing filters
      if (filterPriority !== 'all' && item.priority !== filterPriority) return false;
      
      // Column filters
      if (columnFilters.requestId && !item.requestId.toLowerCase().includes(columnFilters.requestId.toLowerCase())) return false;
      if (columnFilters.item && !item.item.toLowerCase().includes(columnFilters.item.toLowerCase())) return false;
      if (columnFilters.requester && !item.requester.toLowerCase().includes(columnFilters.requester.toLowerCase())) return false;
      if (columnFilters.company && !item.company.toLowerCase().includes(columnFilters.company.toLowerCase())) return false;
      if (columnFilters.budget && !item.budget.toString().includes(columnFilters.budget)) return false;
      if (columnFilters.currency && !item.currency.toLowerCase().includes(columnFilters.currency.toLowerCase())) return false;
      if (columnFilters.priority && !item.priority.toLowerCase().includes(columnFilters.priority.toLowerCase())) return false;
      if (columnFilters.submittedDate && !item.submittedDate.toLowerCase().includes(columnFilters.submittedDate.toLowerCase())) return false;
      
      return true;
    })
    .sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      return 0;
    });



  // Debounced fetch function
  const debouncedFetch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      let abortController: AbortController | null = null;
      
      return async (filters: typeof serverFilters, page: number) => {
        // Cancel previous request
        abortController?.abort();
        
        // Clear previous timeout
        clearTimeout(timeoutId);
        
        // Create new abort controller
        abortController = new AbortController();
        
        // Debounce the API call
        timeoutId = setTimeout(async () => {
          try {
            setLoading(true);
            
            const url = new URL(API_ENDPOINTS.REQUESTS);
            url.searchParams.set('page', page.toString());
            url.searchParams.set('limit', requestsPerPage.toString());
            
            // For requests tab, exclude approved and completed requests
            if (currentView === 'requests') {
              url.searchParams.set('status', 'phase_1,revision_required,define_supplier_list,send_out_rfq,receive_responses');
            }
            
            // Add server-side filters
            if (filters.search) url.searchParams.set('search', filters.search);
            if (filters.urgency) url.searchParams.set('urgency', filters.urgency);
            if (filters.budgetMin) url.searchParams.set('budget_min', filters.budgetMin);
            if (filters.budgetMax) url.searchParams.set('budget_max', filters.budgetMax);
            if (filters.dateFrom) url.searchParams.set('date_from', filters.dateFrom);
            if (filters.dateTo) url.searchParams.set('date_to', filters.dateTo);
            
            // Add column-specific filters
            if (filters.filterRequestId) url.searchParams.set('filter_request_id', filters.filterRequestId);
            if (filters.filterItem) url.searchParams.set('filter_item', filters.filterItem);
            if (filters.filterRequester) url.searchParams.set('filter_requester', filters.filterRequester);
            if (filters.filterCompany) url.searchParams.set('filter_company', filters.filterCompany);
            if (filters.filterBudget) url.searchParams.set('filter_budget', filters.filterBudget);
            if (filters.filterCurrency) url.searchParams.set('filter_currency', filters.filterCurrency);
            if (filters.filterPriority) url.searchParams.set('filter_priority', filters.filterPriority);
            
            const response = await fetch(url.toString(), {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              signal: abortController!.signal,
            });

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log('🔍 AdminTable API Response:', result);
            console.log('🔍 Pagination:', result.pagination);
            
            if (result.success && result.data) {
              // Set pagination info
              if (result.pagination) {
                setTotalPages(result.pagination.pages);
                setTotalRequests(result.pagination.total);
              }
              
              // Transform API data to match AdminTableData interface
              const transformedData = await Promise.all(result.data.map(async (item: Phase1Request) => {
                // Get company name from company_id
                let companyName = 'Individual';
                if (item.company_id && item.company_id !== 'independent') {
                  try {
                    // Fetch company name from backend
                    const companyResponse = await fetch(API_ENDPOINTS.COMPANY_NAME(item.company_id), {
                      headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                      },
                    });
                    
                    if (companyResponse.ok) {
                      const companyData = await companyResponse.json();
                      if (companyData.success) {
                        companyName = companyData.data.name;
                        
                        // Additional check for independent users in frontend
                        if (companyName.startsWith('Independent_User_')) {
                          companyName = '-';
                        }
                      }
                    }
                  } catch (error) {
                    console.error('Error fetching company name:', error);
                    companyName = 'Unknown Company';
                  }
                }
                
                return {
                  id: item._id,
                  requestId: item._id.substring(0, 8).toUpperCase(),
                  item: item.product_name,
                  requester: item.user_name || item.user_info?.name || 'Unknown',
                  company: companyName,
                  budget: item.budget,
                  currency: item.currency || 'USD',
                  submittedDate: item.created_at,
                  priority: item.urgency === 'urgent' ? 'Critical' : 
                           item.urgency === 'high' ? 'High' : 
                           item.urgency === 'normal' ? 'Medium' : 
                           item.urgency === 'low' ? 'Low' : 'Medium'
                };
              }));
              setData(transformedData);
            }
          } catch (error: any) {
            if (error.name === 'AbortError') {
              console.log('Request was aborted');
              return;
            }
            console.error('Error fetching requests:', error);
            setData([]);
          } finally {
            setLoading(false);
          }
        }, 300); // 300ms debounce
      };
    })(),
    [token, requestsPerPage, currentView]
  );

  // Fetch all requests
  useEffect(() => {
    if (token) {
      debouncedFetch(serverFilters, currentPage);
    }
  }, [token, currentPage, serverFilters, debouncedFetch, currentView]);

  const handleViewDetails = (item: any) => {
    // Check if this is a completed request or regular request
    if (currentView === 'completed') {
      // For completed requests, open the completed request detail modal
      setSelectedCompletedRequest(item);
      setIsCompletedModalOpen(true);
    } else {
      // For regular requests, find the original Phase1Request data
      const originalRequest = data.find(d => d.id === item.id);
      if (originalRequest) {
        // Fetch the full request details
        fetch(`${API_ENDPOINTS.REQUESTS}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })
        .then(response => response.json())
        .then(result => {
          if (result.success && result.data) {
            const fullRequest = result.data.find((req: Phase1Request) => req._id === item.id);
            if (fullRequest) {
              setSelectedRequest(fullRequest);
              setIsModalOpen(true);
            }
          }
        })
        .catch(error => {
          console.error('Error fetching request details:', error);
        });
      }
    }
  };



  const handleOpenDetailPage = (item: any) => {
    // Debug log to see what ID we're using
    console.log('🔍 DEBUG - Opening detail page for item:', item);
    console.log('🔍 DEBUG - Using ID:', item._id || item.id);
    
    // Navigate to detail page with request ID
    window.location.href = `/admin/request/${item._id || item.id}`;
  };

  const handleApprove = async (id: string, selectedSuppliers?: any[]) => {
    try {
      const response = await fetch(API_ENDPOINTS.APPROVE_REQUEST(id), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          selected_suppliers: selectedSuppliers || []
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        // Remove the approved request from the list
        setData(prev => prev.filter(req => req.id !== id));
        alert('Request approved successfully!');
      }
    } catch (error) {
      console.error('Error approving request:', error);
      alert('Failed to approve request. Please try again.');
    }
  };

  const handleReject = async (id: string) => {
    const reason = prompt('Please provide a rejection reason:');
    if (!reason) return;

    try {
      const response = await fetch(API_ENDPOINTS.REJECT_REQUEST(id), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ rejection_reason: reason }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        // Remove the rejected request from the list
        setData(prev => prev.filter(req => req.id !== id));
        alert('Request rejected successfully!');
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('Failed to reject request. Please try again.');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Critical': return 'priority-critical';
      case 'High': return 'priority-high';
      case 'Medium': return 'priority-medium';
      default: return 'priority-low';
    }
  };

  return (
    <>
    <div className="admin-table-container">
      <div className="admin-table-header">
        <div className="header-left">
          <h2>{t('common.procurement_requests')} - {t('common.admin_dashboard')}</h2>
        </div>
        <div className="view-toggle">
          <button 
            className="invoice-verification-btn"
            onClick={() => navigate('/invoice')}
            style={{ marginRight: '10px', padding: '8px 16px', backgroundColor: '#ffffff', color: '#333', border: '1px solid #dee2e6', borderRadius: '4px', cursor: 'pointer' }}
          >
            Fatura Doğrulama
          </button>
          <button 
            className={`toggle-btn ${currentView === 'requests' ? 'active' : ''}`}
            onClick={() => toggleView('requests')}
          >
            {t('common.requests')}
          </button>
          <button 
            className={`toggle-btn ${currentView === 'issues' ? 'active' : ''}`}
            onClick={() => toggleView('issues')}
          >
            {t('common.issues')}
          </button>
          <button 
            className={`toggle-btn ${currentView === 'completed' ? 'active' : ''}`}
            onClick={() => toggleView('completed')}
          >
            {t('common.completed')}
          </button>
        </div>
        <div className="header-actions">
          <button 
            className="bulk-import-btn"
            onClick={() => setIsSupplierBulkImportOpen(true)}
            title="Bulk Import Suppliers"
          >
            📥 Import Suppliers
          </button>
        </div>
        <div className="admin-filters">
          {/* Server-side search */}
          <div className="filter-group">
            <label>{t('common.search')}:</label>
            <input
              type="text"
              placeholder={t('common.search_requests')}
              value={serverFilters.search}
              onChange={(e) => handleServerFilterChange('search', e.target.value)}
              className="filter-input"
            />
          </div>
          

          
          {/* Server-side urgency filter */}
          <div className="filter-group">
            <label>{t('common.urgency')}:</label>
            <select 
              value={serverFilters.urgency} 
              onChange={(e) => handleServerFilterChange('urgency', e.target.value)}
            >
              <option value="">{t('common.all_urgency')}</option>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          
          {/* Server-side budget range */}
          <div className="filter-group">
            <label>{t('common.budget')}:</label>
            <input
              type="number"
              placeholder={t('common.min_budget')}
              value={serverFilters.budgetMin}
              onChange={(e) => handleServerFilterChange('budgetMin', e.target.value)}
              className="filter-input-small"
            />
            <input
              type="number"
              placeholder={t('common.max_budget')}
              value={serverFilters.budgetMax}
              onChange={(e) => handleServerFilterChange('budgetMax', e.target.value)}
              className="filter-input-small"
            />
          </div>
          
          <button 
            className="clear-all-filters-btn"
            onClick={clearServerFilters}
            disabled={
              Object.values(serverFilters).every(filter => !filter) &&
              Object.values(columnFilters).every(filter => !filter) &&
              filterPriority === 'all'
            }
          >
            {t('common.clear_filters')}
          </button>
        </div>
      </div>

      <div className="admin-table-wrapper">
        {currentView === 'requests' ? (
          loading ? (
            <div className="dashboard-loading">
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>{t('common.loading_requests')}</p>
              </div>
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>
                    <div className="column-header">
                      <span onClick={() => handleSort('requestId')} className="sortable-header">
                  {t('common.request_id')}
                  {sortField === 'requestId' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                      </span>
                      <button 
                        className={`filter-button ${columnFilters.requestId ? 'active' : ''}`}
                        onClick={() => handleFilterToggle('requestId')}
                      >
                        🔍
                      </button>
                      {activeFilter === 'requestId' && (
                        <div className="filter-dropdown">
                          <input
                            type="text"
                            placeholder={t('common.filter_request_id')}
                            value={columnFilters.requestId}
                            onChange={(e) => handleColumnFilter('requestId', e.target.value)}
                            autoFocus
                          />
                          <div className="filter-actions">
                            <button onClick={() => clearColumnFilter('requestId')}>{t('common.clear')}</button>
                            <button onClick={() => setActiveFilter(null)}>{t('common.close')}</button>
                          </div>
                        </div>
                      )}
                    </div>
                </th>
                  <th>
                    <div className="column-header">
                      <span onClick={() => handleSort('item')} className="sortable-header">
                  {t('common.item')}
                  {sortField === 'item' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                      </span>
                      <button 
                        className={`filter-button ${columnFilters.item ? 'active' : ''}`}
                        onClick={() => handleFilterToggle('item')}
                      >
                        🔍
                      </button>
                      {activeFilter === 'item' && (
                        <div className="filter-dropdown">
                          <input
                            type="text"
                            placeholder={t('common.filter_item')}
                            value={columnFilters.item}
                            onChange={(e) => handleColumnFilter('item', e.target.value)}
                            autoFocus
                          />
                          <div className="filter-actions">
                            <button onClick={() => clearColumnFilter('item')}>{t('common.clear')}</button>
                            <button onClick={() => setActiveFilter(null)}>{t('common.close')}</button>
                          </div>
                        </div>
                      )}
                    </div>
                </th>
                  <th>
                    <div className="column-header">
                      <span onClick={() => handleSort('requester')} className="sortable-header">
                  {t('common.requester')}
                  {sortField === 'requester' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                      </span>
                      <button 
                        className={`filter-button ${columnFilters.requester ? 'active' : ''}`}
                        onClick={() => handleFilterToggle('requester')}
                      >
                        🔍
                      </button>
                      {activeFilter === 'requester' && (
                        <div className="filter-dropdown">
                          <input
                            type="text"
                            placeholder={t('common.filter_requester')}
                            value={columnFilters.requester}
                            onChange={(e) => handleColumnFilter('requester', e.target.value)}
                            autoFocus
                          />
                          <div className="filter-actions">
                            <button onClick={() => clearColumnFilter('requester')}>{t('common.clear')}</button>
                            <button onClick={() => setActiveFilter(null)}>{t('common.close')}</button>
                          </div>
                        </div>
                      )}
                    </div>
                </th>
                  <th>
                    <div className="column-header">
                      <span onClick={() => handleSort('company')} className="sortable-header">
                  {t('common.company')}
                  {sortField === 'company' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                      </span>
                      <button 
                        className={`filter-button ${columnFilters.company ? 'active' : ''}`}
                        onClick={() => handleFilterToggle('company')}
                      >
                        🔍
                      </button>
                      {activeFilter === 'company' && (
                        <div className="filter-dropdown">
                          <input
                            type="text"
                            placeholder={t('common.filter_company')}
                            value={columnFilters.company}
                            onChange={(e) => handleColumnFilter('company', e.target.value)}
                            autoFocus
                          />
                          <div className="filter-actions">
                            <button onClick={() => clearColumnFilter('company')}>{t('common.clear')}</button>
                            <button onClick={() => setActiveFilter(null)}>{t('common.close')}</button>
                          </div>
                        </div>
                      )}
                    </div>
                </th>
                  <th>
                    <div className="column-header">
                      <span onClick={() => handleSort('budget')} className="sortable-header">
                  {t('common.budget')}
                  {sortField === 'budget' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                      </span>
                      <button 
                        className={`filter-button ${columnFilters.budget ? 'active' : ''}`}
                        onClick={() => handleFilterToggle('budget')}
                      >
                        🔍
                      </button>
                      {activeFilter === 'budget' && (
                        <div className="filter-dropdown">
                          <input
                            type="text"
                            placeholder={t('common.filter_budget')}
                            value={columnFilters.budget}
                            onChange={(e) => handleColumnFilter('budget', e.target.value)}
                            autoFocus
                          />
                          <div className="filter-actions">
                            <button onClick={() => clearColumnFilter('budget')}>{t('common.clear')}</button>
                            <button onClick={() => setActiveFilter(null)}>{t('common.close')}</button>
                          </div>
                        </div>
                      )}
                    </div>
                </th>
                  <th>
                    <div className="column-header">
                      <span onClick={() => handleSort('currency')} className="sortable-header">
                  {t('common.currency')}
                  {sortField === 'currency' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                      </span>
                      <button 
                        className={`filter-button ${columnFilters.currency ? 'active' : ''}`}
                        onClick={() => handleFilterToggle('currency')}
                      >
                        🔍
                      </button>
                      {activeFilter === 'currency' && (
                        <div className="filter-dropdown">
                          <input
                            type="text"
                            placeholder={t('common.filter_currency')}
                            value={columnFilters.currency}
                            onChange={(e) => handleColumnFilter('currency', e.target.value)}
                            autoFocus
                          />
                          <div className="filter-actions">
                            <button onClick={() => clearColumnFilter('currency')}>{t('common.clear')}</button>
                            <button onClick={() => setActiveFilter(null)}>{t('common.close')}</button>
                          </div>
                        </div>
                      )}
                    </div>
                </th>
                  <th>
                    <div className="column-header">
                      <span onClick={() => handleSort('priority')} className="sortable-header">
                  {t('common.priority')}
                  {sortField === 'priority' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                      </span>
                      <button 
                        className={`filter-button ${columnFilters.priority ? 'active' : ''}`}
                        onClick={() => handleFilterToggle('priority')}
                      >
                        🔍
                      </button>
                      {activeFilter === 'priority' && (
                        <div className="filter-dropdown">
                          <input
                            type="text"
                            placeholder={t('common.filter_priority')}
                            value={columnFilters.priority}
                            onChange={(e) => handleColumnFilter('priority', e.target.value)}
                            autoFocus
                          />
                          <div className="filter-actions">
                            <button onClick={() => clearColumnFilter('priority')}>{t('common.clear')}</button>
                            <button onClick={() => setActiveFilter(null)}>{t('common.close')}</button>
                          </div>
                        </div>
                      )}
                    </div>
                </th>

                  <th>
                    <div className="column-header">
                      <span onClick={() => handleSort('submittedDate')} className="sortable-header">
                  {t('common.submitted_date')}
                  {sortField === 'submittedDate' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                      </span>
                      <button 
                        className={`filter-button ${columnFilters.submittedDate ? 'active' : ''}`}
                        onClick={() => handleFilterToggle('submittedDate')}
                      >
                        🔍
                      </button>
                      {activeFilter === 'submittedDate' && (
                        <div className="filter-dropdown">
                          <input
                            type="text"
                            placeholder={t('common.filter_date')}
                            value={columnFilters.submittedDate}
                            onChange={(e) => handleColumnFilter('submittedDate', e.target.value)}
                            autoFocus
                          />
                          <div className="filter-actions">
                            <button onClick={() => clearColumnFilter('submittedDate')}>{t('common.clear')}</button>
                            <button onClick={() => setActiveFilter(null)}>{t('common.close')}</button>
                          </div>
                        </div>
                      )}
                    </div>
                </th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedAndFilteredData.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="no-data-cell">
                      <div className="no-data-content">
                        <p>{t('common.no_requests_found')}</p>
                        <p className="no-data-hint">{t('common.try_adjusting_filters')}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  sortedAndFilteredData.map((item) => (
                    <tr 
                      key={item.id} 
                      className="clickable-row"
                      onClick={() => handleViewDetails(item)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="request-id">{item.requestId}</td>
                      <td className="item-name">{item.item}</td>
                      <td>{item.requester}</td>
                      <td className="company">{item.company}</td>
                      <td className="budget">{item.budget.toLocaleString()}</td>
                      <td className="currency">{item.currency}</td>
                      <td>
                        <span className={`priority-badge ${getPriorityColor(item.priority)}`}>
                          {item.priority}
                        </span>
                      </td>
                      <td>{new Date(item.submittedDate).toLocaleDateString()}</td>
                      <td className="actions">
                        <button 
                          className="action-btn view-btn"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent row click when button is clicked
                            handleViewDetails(item);
                          }}
                          title="View Details"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                        </button>
                        <button 
                          className="action-btn page-btn"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent row click when button is clicked
                            handleOpenDetailPage(item);
                          }}
                          title="Open Detail Page"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14,2 14,8 20,8"/>
                            <line x1="16" y1="13" x2="8" y2="13"/>
                            <line x1="16" y1="17" x2="8" y2="17"/>
                            <polyline points="10,9 9,9 8,9"/>
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )
        ) : currentView === 'issues' ? (
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t('common.issue_title')}</th>
                <th>{t('common.issue_category')}</th>
                <th>{t('common.issue_priority')}</th>
                <th>{t('common.status')}</th>
                <th>{t('common.reported_by')}</th>
                <th>{t('common.company')}</th>
                <th>{t('common.date')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {issuesLoading ? (
                <tr>
                  <td colSpan={8} className="loading-cell">
                    <div className="loading-spinner">{t('common.loading')}</div>
                  </td>
                </tr>
              ) : issues.length === 0 ? (
                <tr>
                  <td colSpan={8} className="no-issues">
                    <p>{t('common.no_issues_found')}</p>
                  </td>
                </tr>
              ) : (
                issues.map((issue) => (
                  <tr key={issue._id}>
                    <td className="issue-title">{issue.title}</td>
                    <td>
                      <span className={`category-badge ${issue.category}`}>
                        {t(`common.issue_category_${issue.category}`)}
                      </span>
                    </td>
                    <td>
                      <span className={`priority-badge ${getPriorityColor(issue.priority)}`}>
                        {t(`common.issue_priority_${issue.priority}`)}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${issue.status === 'open' ? 'open' : 'resolved'}`}>
                        {issue.status === 'open' ? t('common.open') : t('common.resolved')}
                      </span>
                    </td>
                    <td>
                      <div className="user-info">
                        <div className="user-name">{issue.user_name}</div>
                        <div className="user-email">{issue.user_email}</div>
                      </div>
                    </td>
                    <td>{issue.company_name || t('common.independent_user')}</td>
                    <td>{new Date(issue.timestamp).toLocaleDateString()}</td>
                    <td className="actions">
                      <button 
                        className="action-btn view-btn"
                        onClick={() => {/* TODO: Add issue detail modal */}}
                        title={t('common.view_details')}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t('common.request_id')}</th>
                <th>{t('common.item')}</th>
                <th>{t('common.requester')}</th>
                <th>{t('common.company')}</th>
                <th>{t('common.budget')}</th>
                <th>{t('common.currency')}</th>
                <th>{t('common.priority')}</th>
                <th>{t('common.completed_date')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {completedLoading ? (
                <tr>
                  <td colSpan={9} className="loading-cell">
                    <div className="loading-spinner">{t('common.loading')}</div>
                  </td>
                </tr>
              ) : completedRequests.length === 0 ? (
                <tr>
                  <td colSpan={9} className="no-data-cell">
                    <div className="no-data-content">
                      <p>{t('common.no_completed_requests_found')}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                completedRequests.map((item) => (
                  <tr key={item._id || item.id} className="clickable-row">
                                          <td className="request-id">{item.specific_id || item._id || 'N/A'}</td>
                    <td className="item-name">{item.product_name || 'N/A'}</td>
                    <td>{item.user_name || 'N/A'}</td>
                                          <td className="company">
                        {companyNames[item.company_id] === '-' ? 'Independent User' : 
                         companyNames[item.company_id] || item.company_id || 'N/A'}
                      </td>
                    <td className="budget">{item.budget ? item.budget.toLocaleString() : 'N/A'}</td>
                    <td className="currency">{item.currency || 'N/A'}</td>
                    <td>
                      <span className={`priority-badge ${getPriorityColor(item.urgency || item.importance || 'medium')}`}>
                        {item.urgency || item.importance || 'Medium'}
                      </span>
                    </td>
                    <td>{item.completed_at ? new Date(item.completed_at).toLocaleDateString() : 'N/A'}</td>
                                                <td className="actions">
                              <button 
                                className="action-btn view-btn"
                                onClick={() => handleViewDetails(item)}
                                title={t('common.view_details')}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                  <circle cx="12" cy="12" r="3"/>
                                </svg>
                              </button>
                            </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {currentView === 'requests' && !loading && sortedAndFilteredData.length === 0 && (
        <div className="no-data">
          <p>{t('common.no_requests_found')}</p>
        </div>
      )}
      
      {currentView === 'issues' && !issuesLoading && issues.length === 0 && (
        <div className="no-data">
          <p>{t('common.no_issues_found')}</p>
        </div>
      )}

      {currentView === 'completed' && !completedLoading && completedRequests.length === 0 && (
        <div className="no-data">
          <p>{t('common.no_completed_requests_found')}</p>
        </div>
      )}

      {/* Pagination for requests */}
      {currentView === 'requests' && !loading && totalRequests > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            {t('common.showing')} {((currentPage - 1) * requestsPerPage) + 1} - {Math.min(currentPage * requestsPerPage, totalRequests)} {t('common.of')} {totalRequests} {t('common.requests')}
          </div>
          <div className="pagination-controls">
            <button 
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || loading}
            >
              {loading ? t('common.loading') : t('common.previous')}
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
              return (
                <button
                  key={pageNum}
                  className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => handlePageChange(pageNum)}
                  disabled={loading}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button 
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || loading}
            >
              {loading ? t('common.loading') : t('common.next')}
            </button>
          </div>
        </div>
      )}
    </div>

      <AdminRequestModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedRequest(null);
        }}
        request={selectedRequest}
        onApprove={handleApprove}
        onReject={handleReject}
      />

      <CompletedRequestDetailModal
        isOpen={isCompletedModalOpen}
        onClose={() => {
          setIsCompletedModalOpen(false);
          setSelectedCompletedRequest(null);
        }}
        request={selectedCompletedRequest}
      />

      <SupplierBulkImportModal
        isOpen={isSupplierBulkImportOpen}
        onClose={() => setIsSupplierBulkImportOpen(false)}
        onImportSuccess={() => {
          // Refresh data if needed
          console.log('Suppliers imported successfully');
        }}
      />

    </>
  );
};

export default AdminTable; 